import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, act } from '@ngrx/effects';
import {
  switchMap,
  map,
  mergeMap,
  catchError,
  exhaustMap,
  concatMap,
} from 'rxjs/operators';
import { of } from 'rxjs';

import {
  loadCampaigns,
  loadCampaignsSuccess,
  changePage,
  changePageSuccess,
  updateCampaign,
  updateCampaignSuccess,
  updateCampaignError,
  refreshCampaigns,
  getCampaignInfo,
  getCampaignInfoSuccess,
  getCampaignInfoError,
  createCampaign,
  createCampaignSuccess,
  createCampaignError,
  deleteCampaign,
  deleteCampaignSuccess,
  deleteCampaignError,
  restoreCampaign,
  restoreCampaignSuccess,
  restoreCampaignError,
} from '../actions/campaigns.action';
import { CampaignService } from '../services/campaign.service';
import { ECampaignPaginationType, ICampaign } from '../models/campaigns.model';

@Injectable()
export class CampaignEffect {
  loadCampaigns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCampaigns),
      switchMap(({ pagination, activePagination }) =>
        this.campaignsService.getAllCampaigns(pagination, activePagination)
      ),
      map(res => {
        return loadCampaignsSuccess({ ...res });
      })
    )
  );

  refreshCampaigns = createEffect(() =>
    this.actions$.pipe(
      ofType(refreshCampaigns),
      switchMap(action =>
        of(
          loadCampaigns({
            pagination: null,
            activePagination: action.pagination,
          })
        )
      )
    )
  );

  updateCampaign$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCampaign),
      switchMap(action =>
        this.campaignsService.updateCampaign(action.campaign).pipe(
          map(res => updateCampaignSuccess({ response: res })),
          catchError(error => of(updateCampaignError(error)))
        )
      )
    )
  );

  deleteCampaign$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCampaign),
      switchMap(action =>
        this.campaignsService.deleteCampaign(action.campaign).pipe(
          map(res =>
            deleteCampaignSuccess({
              response: res,
            })
          ),
          catchError(err =>
            of(
              deleteCampaignError({
                error: err,
              })
            )
          )
        )
      )
    )
  );

  restoreCampaign$ = createEffect(() =>
    this.actions$.pipe(
      ofType(restoreCampaign),
      switchMap(action =>
        this.campaignsService.restoreCampaign(action.campaign).pipe(
          map(res =>
            restoreCampaignSuccess({
              response: res,
            })
          ),
          catchError(err =>
            of(
              restoreCampaignError({
                error: err,
              })
            )
          )
        )
      )
    )
  );

  changePage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changePage),
      mergeMap(action => {
        switch (action.campaign_type) {
          case ECampaignPaginationType.ACTIVE:
            return this.campaignsService
              .getActiveCampaigns({
                current_page: action.page,
                page_size: action.page_size,
              })
              .pipe(
                map(res => {
                  res.type = action.campaign_type;

                  return res;
                })
              );
          case ECampaignPaginationType.PENDING:
            return this.campaignsService
              .getPendingCampaigns({
                current_page: action.page,
                page_size: action.page_size,
              })
              .pipe(
                map(res => {
                  res.type = action.campaign_type;

                  return res;
                })
              );
          default:
            return this.campaignsService
              .getDeletedCampaigns({
                current_page: action.page,
                page_size: action.page_size,
              })
              .pipe(
                map(res => {
                  res.type = action.campaign_type;

                  return res;
                })
              );
        }
      }),
      map(res => {
        return changePageSuccess({
          campaign_type: res.type as ECampaignPaginationType,
          items: res.items,
          pagination: {
            current_page: res.current_page,
            page_size: res.current_page,
            has_more_data: res.has_more_data,
            total_pages_count: res.total_page_count,
            total_count: res.total_count,
          },
        });
      })
    )
  );

  getCampaignInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCampaignInfo),
      switchMap(action =>
        this.campaignsService.getCampaignInfo(action.campaign_id)
      ),
      map((campaign: ICampaign) => getCampaignInfoSuccess({ campaign })),
      catchError(error => of(getCampaignInfoError({ error })))
    )
  );

  createCampaign$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createCampaign),
      switchMap(action =>
        this.campaignsService
          .createCampaign(
            action.campaign_name,
            action.campaign_type,
            action.campaign_frequency
          )
          .pipe(
            map(result =>
              createCampaignSuccess({ campaign_id: result.campaign_id })
            ),
            catchError(error => of(createCampaignError({ error })))
          )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private campaignsService: CampaignService
  ) {}
}
